import {
    useEffect,
    useState
} from "react";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    type IInputGroupProps,
    InputGroup,
    Button,
    Kbd
} from "ncore-ui-kit";
import {
    InlineCode,
    BulletList,
    Paragraph,
    CodeBlock,
    Article,
    Preview,
    Section
} from "../../components/content";
import {
    SearchIcon,
    StarIcon,
    PlusIcon,
    FileIcon
} from "../../anatomy/icons";

const NAME = "InputGroup";

const USAGE_CODE = `const [query, setQuery] = useState("");

<InputGroup
    spreadBehaviour="stretch"
    title="Search"
>
    <InputGroup.Addon
        icon={SearchIcon}
    />
    <InputGroup.Input
        placeholder="Search products"
        onChangeText={setQuery}
        value={query}
    />
    <InputGroup.Addon
        align="end"
    >
        <InputGroup.Button
            onPress={() => search(query)}
            title="Search"
        />
    </InputGroup.Addon>
</InputGroup>`;

const MULTILINE_CODE = `<InputGroup
    isShowCounter={true}
    title="Message"
    maxLength={140}
>
    <InputGroup.TextArea
        onChangeText={setMessage}
        submitBehaviour="modEnter"
        onSubmit={send}
        value={message}
    />
    <InputGroup.Addon
        align="blockEnd"
    >
        <InputGroup.Button
            icon={FileIcon}
            title="Attach"
        />
        <Kbd
            keys="mod+Enter"
        />
        <Button
            onPress={send}
            title="Send"
            size="small"
        />
    </InputGroup.Addon>
</InputGroup>`;

const TOGGLE_CODE = `const [isMatchCase, setIsMatchCase] = useState(true);

<InputGroup.Button
    onPress={() => setIsMatchCase(!isMatchCase)}
    isPressed={isMatchCase}
    title="Match case"
/>`;

const SIZES = [
    "small",
    "medium",
    "large"
] as const;

const INVITE_LINK = "ncore.app/invite/7KX2";

const BOOKMARK_LINK = "ncore.app/docs/input-group";

const COPIED_DELAY = 1600;

const MESSAGE_MAX_LENGTH = 140;

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("inputGroupPage.addonsTitle"),
        id: "addons"
    },
    {
        title: t("inputGroupPage.buttonsTitle"),
        id: "buttons"
    },
    {
        title: t("inputGroupPage.multilineTitle"),
        id: "multiline"
    },
    {
        title: t("section.sizes"),
        id: "sizes"
    },
    {
        title: t("section.states"),
        id: "states"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const InputGroupPage = () => {
    const {
        t
    } = useDocsI18n();

    const [
        query,
        setQuery
    ] = useState("");

    const [
        searchedQuery,
        setSearchedQuery
    ] = useState("");

    const [
        isCopied,
        setIsCopied
    ] = useState(false);

    const [
        tag,
        setTag
    ] = useState("");

    const [
        tags,
        setTags
    ] = useState<Array<string>>([]);

    const [
        message,
        setMessage
    ] = useState("");

    const [
        sentMessage,
        setSentMessage
    ] = useState("");

    const [
        isMatchCase,
        setIsMatchCase
    ] = useState(true);

    const [
        isFavourite,
        setIsFavourite
    ] = useState(false);

    useEffect(() => {
        if(!isCopied) {
            return;
        }

        const timer = window.setTimeout(() => setIsCopied(false), COPIED_DELAY);

        return () => {
            window.clearTimeout(timer);
        };
    }, [
        isCopied
    ]);

    const isMessageSendable = message.trim() !== "" && message.length <= MESSAGE_MAX_LENGTH;

    const handleSearch = () => {
        setSearchedQuery(query);
    };

    const handleCopy = () => {
        navigator.clipboard?.writeText(INVITE_LINK)?.catch(() => undefined);

        setIsCopied(true);
    };

    const handleToggleMatchCase = () => {
        setIsMatchCase((currentValue) => !currentValue);
    };

    const handleToggleFavourite = () => {
        setIsFavourite((currentValue) => !currentValue);
    };

    const handleAddTag = () => {
        const nextTag = tag.trim();

        if(nextTag === "") {
            return;
        }

        setTags((currentTags) => [
            ...currentTags,
            nextTag
        ]);

        setTag("");
    };

    const handleSend = () => {
        if(!isMessageSendable) {
            return;
        }

        setSentMessage(message.trim());

        setMessage("");
    };

    const renderAddress = (title: string, props: Partial<IInputGroupProps>) => <InputGroup
        {...props}
        spreadBehaviour="stretch"
        title={title}
    >
        <InputGroup.Addon
            variant="attached"
        >
            <InputGroup.Text>
                https://
            </InputGroup.Text>
        </InputGroup.Addon>
        <InputGroup.Input
            defaultValue="nibgat"
        />
    </InputGroup>;

    return <Article>
        <ComponentHeader
            lead={t("inputGroupPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("inputGroupPage.overviewText")}
            </Paragraph>
            <Paragraph>
                {t("inputGroupPage.overviewAlternatives")}
            </Paragraph>
            <Preview
                caption={t("inputGroupPage.overviewCaption")}
                isStacked={true}
            >
                <InputGroup
                    hintText={searchedQuery ? t("inputGroupPage.searchedFor", [
                        searchedQuery
                    ]) : undefined}
                    title={t("sample.search")}
                    spreadBehaviour="stretch"
                >
                    <InputGroup.Addon
                        icon={SearchIcon}
                    />
                    <InputGroup.Input
                        placeholder={t("inputGroupPage.searchPlaceholder")}
                        onChangeText={setQuery}
                        value={query}
                    />
                    <InputGroup.Addon
                        align="end"
                    >
                        <InputGroup.Button
                            title={t("sample.search")}
                            onPress={handleSearch}
                        />
                    </InputGroup.Addon>
                </InputGroup>
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("inputGroupPage.addonsTitle")}
            id="addons"
        >
            <Paragraph>
                {t("inputGroupPage.addonsText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <InputGroup
                    title={t("inputGroupPage.address")}
                    spreadBehaviour="stretch"
                >
                    <InputGroup.Addon
                        variant="attached"
                    >
                        <InputGroup.Text>
                            https://
                        </InputGroup.Text>
                    </InputGroup.Addon>
                    <InputGroup.Input
                        defaultValue="nibgat"
                    />
                    <InputGroup.Addon
                        variant="attached"
                        align="end"
                    >
                        <InputGroup.Text>
                            .ncore.app
                        </InputGroup.Text>
                    </InputGroup.Addon>
                </InputGroup>
                <InputGroup
                    title={t("inputGroupPage.budget")}
                    spreadBehaviour="stretch"
                >
                    <InputGroup.Addon>
                        <InputGroup.Text>
                            {t("inputGroupPage.currency")}
                        </InputGroup.Text>
                    </InputGroup.Addon>
                    <InputGroup.Input
                        keyboardType="decimal-pad"
                        defaultValue="1250"
                    />
                    <InputGroup.Addon
                        align="end"
                    >
                        <InputGroup.Text>
                            {t("inputGroupPage.perMonth")}
                        </InputGroup.Text>
                    </InputGroup.Addon>
                </InputGroup>
                <InputGroup
                    title={t("inputGroupPage.shortcutTitle")}
                    spreadBehaviour="stretch"
                >
                    <InputGroup.Addon
                        icon={SearchIcon}
                    />
                    <InputGroup.Input
                        placeholder={t("inputGroupPage.shortcutPlaceholder")}
                    />
                    <InputGroup.Addon
                        align="end"
                    >
                        <Kbd
                            keys="mod+K"
                        />
                    </InputGroup.Addon>
                </InputGroup>
            </Preview>
        </Section>
        <Section
            title={t("inputGroupPage.buttonsTitle")}
            id="buttons"
        >
            <Paragraph>
                {t("inputGroupPage.buttonsText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <InputGroup
                    title={t("inputGroupPage.invite")}
                    spreadBehaviour="stretch"
                    isReadOnly={true}
                >
                    <InputGroup.Input
                        defaultValue={INVITE_LINK}
                    />
                    <InputGroup.Addon
                        align="end"
                    >
                        <InputGroup.Button
                            title={isCopied ? t("inputGroupPage.copied") : t("inputGroupPage.copy")}
                            isShowWhileReadOnly={true}
                            onPress={handleCopy}
                        />
                    </InputGroup.Addon>
                </InputGroup>
                <InputGroup
                    title={t("inputGroupPage.newsletter")}
                    spreadBehaviour="stretch"
                >
                    <InputGroup.Input
                        placeholder={t("sample.placeholder")}
                        keyboardType="email-address"
                    />
                    <InputGroup.Addon
                        align="end"
                    >
                        <InputGroup.Button
                            title={t("inputGroupPage.subscribe")}
                            type="primary"
                        />
                    </InputGroup.Addon>
                </InputGroup>
                <InputGroup
                    hintText={tags.length ? t("inputGroupPage.tagsAdded", [
                        tags.join(", ")
                    ]) : undefined}
                    title={t("inputGroupPage.tagsTitle")}
                    spreadBehaviour="stretch"
                >
                    <InputGroup.Input
                        placeholder={t("inputGroupPage.tagPlaceholder")}
                        onChangeText={setTag}
                        value={tag}
                    />
                    <InputGroup.Addon
                        align="end"
                    >
                        <InputGroup.Button
                            title={t("inputGroupPage.add")}
                            onPress={handleAddTag}
                            icon={PlusIcon}
                        />
                    </InputGroup.Addon>
                </InputGroup>
                <InputGroup
                    title={t("sample.search")}
                    spreadBehaviour="stretch"
                >
                    <InputGroup.Addon
                        icon={SearchIcon}
                    />
                    <InputGroup.Input
                        placeholder={t("inputGroupPage.searchPlaceholder")}
                    />
                    <InputGroup.Addon
                        align="end"
                    >
                        <InputGroup.Button
                            title={t("inputGroupPage.matchCase")}
                            onPress={handleToggleMatchCase}
                            isPressed={isMatchCase}
                        />
                    </InputGroup.Addon>
                </InputGroup>
                <InputGroup
                    title={t("inputGroupPage.bookmark")}
                    spreadBehaviour="stretch"
                >
                    <InputGroup.Input
                        defaultValue={BOOKMARK_LINK}
                    />
                    <InputGroup.Addon
                        align="end"
                    >
                        <InputGroup.Button
                            title={t("inputGroupPage.favourite")}
                            onPress={handleToggleFavourite}
                            isPressed={isFavourite}
                            icon={StarIcon}
                        />
                    </InputGroup.Addon>
                </InputGroup>
            </Preview>
            <CodeBlock
                code={TOGGLE_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("inputGroupPage.multilineTitle")}
            id="multiline"
        >
            <Paragraph>
                {t("inputGroupPage.multilineText")}
            </Paragraph>
            <Preview
                caption={t("inputGroupPage.multilineCaption")}
                isStacked={true}
            >
                <InputGroup
                    hintText={sentMessage ? t("inputGroupPage.sentMessage", [
                        sentMessage
                    ]) : undefined}
                    title={t("inputGroupPage.messageTitle")}
                    maxLength={MESSAGE_MAX_LENGTH}
                    spreadBehaviour="stretch"
                    isShowCounter={true}
                >
                    <InputGroup.TextArea
                        placeholder={t("inputGroupPage.messagePlaceholder")}
                        onChangeText={setMessage}
                        onSubmit={handleSend}
                        value={message}
                    />
                    <InputGroup.Addon
                        align="blockEnd"
                    >
                        <InputGroup.Button
                            title={t("inputGroupPage.attach")}
                            icon={FileIcon}
                        />
                        <Kbd
                            keys="mod+Enter"
                        />
                        <Button
                            title={t("inputGroupPage.send")}
                            isDisabled={!isMessageSendable}
                            onPress={handleSend}
                            size="small"
                        />
                    </InputGroup.Addon>
                </InputGroup>
            </Preview>
            <CodeBlock
                code={MULTILINE_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("section.sizes")}
            id="sizes"
        >
            <Paragraph>
                {t("inputGroupPage.sizesText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                {
                    SIZES.map((size) => <InputGroup
                        spreadBehaviour="stretch"
                        title={size}
                        size={size}
                        key={size}
                    >
                        <InputGroup.Addon
                            icon={SearchIcon}
                        />
                        <InputGroup.Input
                            placeholder={t("sample.search")}
                        />
                        <InputGroup.Addon
                            align="end"
                        >
                            <InputGroup.Button
                                title={t("sample.search")}
                            />
                        </InputGroup.Addon>
                    </InputGroup>)
                }
            </Preview>
        </Section>
        <Section
            title={t("section.states")}
            id="states"
        >
            <Paragraph>
                {t("inputGroupPage.statesText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                {
                    renderAddress(t("inputGroupPage.disabled"), {
                        isDisabled: true
                    })
                }
                {
                    renderAddress(t("inputGroupPage.readOnly"), {
                        isReadOnly: true
                    })
                }
                {
                    renderAddress(t("inputGroupPage.loading"), {
                        isLoading: true
                    })
                }
                <InputGroup
                    title={t("inputGroupPage.loadingMultiline")}
                    spreadBehaviour="stretch"
                    isLoading={true}
                >
                    <InputGroup.TextArea
                        placeholder={t("inputGroupPage.messagePlaceholder")}
                    />
                    <InputGroup.Addon
                        align="blockEnd"
                    >
                        <InputGroup.Button
                            title={t("inputGroupPage.attach")}
                            icon={FileIcon}
                        />
                    </InputGroup.Addon>
                </InputGroup>
                {
                    renderAddress("danger", {
                        hintText: t("inputGroupPage.hintDanger"),
                        type: "danger"
                    })
                }
            </Preview>
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("inputGroupPage.a11yLabel"),
                    t("inputGroupPage.a11yGroup"),
                    t("inputGroupPage.a11yText"),
                    t("inputGroupPage.a11yButton"),
                    t("inputGroupPage.a11yStates"),
                    t("inputGroupPage.a11yCounter"),
                    t("inputGroupPage.a11ySubmit"),
                    t("inputGroupPage.a11yParts")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("inputGroupPage.propTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("inputGroupPage.propHintText"),
                        name: "hintText",
                        type: "string"
                    },
                    {
                        type: "\"neutral\" | \"danger\" | \"warning\" | \"success\" | \"info\"",
                        description: t("inputGroupPage.propType"),
                        defaultValue: "\"neutral\"",
                        name: "type"
                    },
                    {
                        type: "\"small\" | \"medium\" | \"large\"",
                        description: t("inputGroupPage.propSize"),
                        name: "size"
                    },
                    {
                        description: t("inputGroupPage.propSpreadBehaviour"),
                        type: "\"baseline\" | \"stretch\" | \"free\"",
                        name: "spreadBehaviour"
                    },
                    {
                        description: t("inputGroupPage.propChildren"),
                        type: "ReactNode",
                        name: "children"
                    },
                    {
                        description: t("inputGroupPage.propIsDisabled"),
                        defaultValue: "false",
                        name: "isDisabled",
                        type: "boolean"
                    },
                    {
                        description: t("inputGroupPage.propIsReadOnly"),
                        defaultValue: "false",
                        name: "isReadOnly",
                        type: "boolean"
                    },
                    {
                        description: t("inputGroupPage.propIsLoading"),
                        defaultValue: "false",
                        name: "isLoading",
                        type: "boolean"
                    },
                    {
                        description: t("inputGroupPage.propIsRequired"),
                        defaultValue: "false",
                        name: "isRequired",
                        type: "boolean"
                    },
                    {
                        description: t("inputGroupPage.propIsOptional"),
                        defaultValue: "false",
                        name: "isOptional",
                        type: "boolean"
                    },
                    {
                        description: t("inputGroupPage.propMaxLength"),
                        name: "maxLength",
                        type: "number"
                    },
                    {
                        description: t("inputGroupPage.propIsShowCounter"),
                        defaultValue: "false",
                        name: "isShowCounter",
                        type: "boolean"
                    },
                    {
                        description: t("inputGroupPage.propHintTextIcon"),
                        type: "NCoreUIKitIcon",
                        name: "hintTextIcon"
                    },
                    {
                        description: t("inputGroupPage.propIsShowHintTextIcon"),
                        name: "isShowHintTextIcon",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        description: t("inputGroupPage.propId"),
                        type: "string",
                        name: "id"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("inputGroupPage.propCustomTheme"),
                        name: "customTheme"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("inputGroupPage.propsNote")}
            </Paragraph>
            <Paragraph>
                <InlineCode>
                    InputGroup.Addon
                </InlineCode>
                {" "}
                {t("inputGroupPage.addonPropsText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        type: "\"start\" | \"end\" | \"blockStart\" | \"blockEnd\"",
                        description: t("inputGroupPage.addonAlign"),
                        defaultValue: "\"start\"",
                        name: "align"
                    },
                    {
                        description: t("inputGroupPage.addonVariant"),
                        type: "\"inline\" | \"attached\"",
                        defaultValue: "\"inline\"",
                        name: "variant"
                    },
                    {
                        description: t("inputGroupPage.addonIcon"),
                        type: "NCoreUIKitIcon",
                        name: "icon"
                    },
                    {
                        description: t("inputGroupPage.addonChildren"),
                        type: "ReactNode",
                        name: "children"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("inputGroupPage.addonCustomTheme"),
                        name: "customTheme"
                    }
                ]}
            />
            <Paragraph>
                <InlineCode>
                    InputGroup.Button
                </InlineCode>
                {" "}
                {t("inputGroupPage.buttonPropsText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("inputGroupPage.buttonTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("inputGroupPage.buttonOnPress"),
                        type: "() => void",
                        name: "onPress"
                    },
                    {
                        description: t("inputGroupPage.buttonIcon"),
                        type: "NCoreUIKitIcon",
                        name: "icon"
                    },
                    {
                        description: t("inputGroupPage.buttonType"),
                        type: "\"neutral\" | \"primary\"",
                        defaultValue: "\"neutral\"",
                        name: "type"
                    },
                    {
                        description: t("inputGroupPage.buttonIsDisabled"),
                        name: "isDisabled",
                        type: "boolean"
                    },
                    {
                        description: t("inputGroupPage.buttonIsPressed"),
                        name: "isPressed",
                        type: "boolean"
                    },
                    {
                        description: t("inputGroupPage.buttonIsShowWhileReadOnly"),
                        name: "isShowWhileReadOnly",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        description: t("inputGroupPage.buttonIsRefocusEnabled"),
                        name: "isRefocusEnabled",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("inputGroupPage.buttonCustomTheme"),
                        name: "customTheme"
                    }
                ]}
            />
            <Paragraph>
                <InlineCode>
                    InputGroup.TextArea
                </InlineCode>
                {" "}
                {t("inputGroupPage.textAreaPropsText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("inputGroupPage.textAreaSubmitBehaviour"),
                        type: "\"enter\" | \"modEnter\" | \"none\"",
                        defaultValue: "\"modEnter\"",
                        name: "submitBehaviour"
                    },
                    {
                        description: t("inputGroupPage.textAreaOnSubmit"),
                        type: "(value: string) => void",
                        name: "onSubmit"
                    },
                    {
                        description: t("inputGroupPage.textAreaMinRows"),
                        defaultValue: "2",
                        name: "minRows",
                        type: "number"
                    },
                    {
                        description: t("inputGroupPage.textAreaMaxRows"),
                        defaultValue: "8",
                        name: "maxRows",
                        type: "number"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("inputGroupPage.partsNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default InputGroupPage;
